import React, { useEffect, useState } from "react";
import { Table, Typography, Spin, Image, Tag } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const PackagesViewer = ({ shippingOrderId, refreshKey, packagesList, rowSelection }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(!packagesList);
  const { t } = useTranslation();

  useEffect(() => {
    if (packagesList) {
      setPackages(packagesList);
      setLoading(false);
      return;
    }
    
    if (!shippingOrderId) return;
    
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `/shipping/api/v1/shipping/order-shipping/${shippingOrderId}/packages/`
        );
        setPackages(res.data);
      } catch (error) {
        console.error("Failed to fetch packages", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [shippingOrderId, refreshKey, packagesList]);

  if (loading) {
    return <Spin style={{ margin: "20px auto", display: "block" }} />;
  }

  if (!packages || packages.length === 0) {
    return null; // Don't show anything if there are no packages
  }

  const columns = [
    {
      title: t("packing.boxNumber", { defaultValue: "Box #" }),
      key: "boxNumber",
      render: (_, __, index) => <Text strong>{index + 1}</Text>,
      width: 80,
    },
    {
      title: t("packing.photo", { defaultValue: "Photo" }),
      dataIndex: "photo_url",
      key: "photo_url",
      render: (url, _, index) =>
        url ? (
          <Image
            src={url}
            alt={`Box ${index + 1}`}
            height={40}
            style={{ objectFit: "cover", borderRadius: "4px" }}
          />
        ) : (
          t("table.common.na")
        ),
      width: 100,
    },
    {
      title: t("packing.weight", { defaultValue: "Weight (kg)" }),
      dataIndex: "weight_kg",
      key: "weight_kg",
      render: (val) => `${val} kg`,
      width: 120,
    },
    {
      title: t("packing.dimensions", { defaultValue: "Dimensions (cm)" }),
      key: "dimensions",
      render: (_, record) =>
        `${record.length_cm} x ${record.width_cm} x ${record.height_cm} cm`,
    },
    {
      title: t("packing.items", { defaultValue: "Items" }),
      key: "items",
      render: (_, record) => (
        <Tag color="blue">{record.items?.length || 0} items</Tag>
      ),
      width: 100,
    },
    {
      title: t("packing.packedBy", { defaultValue: "Packed by" }),
      dataIndex: "confirmed_by_name",
      key: "confirmed_by_name",
      render: (name) => name || t("table.common.na"),
    },
  ];

  return (
    <div style={{ marginTop: "24px" }}>
      <Typography.Title level={5} style={{ marginBottom: "16px" }}>
        {t("packing.recordedPackages", { defaultValue: "Recorded Packages" })}
      </Typography.Title>
      <Table
        dataSource={packages}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
        rowSelection={rowSelection}
      />
    </div>
  );
};

export default PackagesViewer;
